import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  InputBase,
  IconButton,
  Avatar,
  Button,
  Grid,
} from "@mui/material";
import { Send as SendIcon, Cached as CachedIcon, Person as PersonIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { authSelector } from "../../services/redux/slices/authSlice";
import {
  currentSessionId,
  setCurrentSessionId,
} from "../../services/redux/slices/chatSlice";
import chatServices from "../../services/api/service/chatService";
import { AppDispatch, RootState } from "../../services/redux/store";
import { decrypt } from "../../utils/crypto";
import { formatTimeAgo } from "../../utils/dateFormatUtils";

// --- CONFIGURATION ---
const DEFAULT_MESSAGE_LIMIT = 50;

// --- INTERFACE DEFINITIONS ---
interface ChatMessage {
  id: string;
  message: string;
  source: "customer" | "sales";
  created_at: string;
  avatar?: string;
}
interface PreviousChat {
  id: string;
  title: string;
  messages: ChatMessage[];
  created_at: string;
}
interface ConnectWithSalesProps {}

// animation for refresh button icon
const spinAnimation = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ChatMessageBubble: React.FC<{
  message: ChatMessage;
}> = ({ message }) => {
  const isCurrentUserIsCustomer = message?.source === "customer";
  // const salesAvatarPath = "/path/to/sales/avatar.png";

  const borderRadiusStyle = {
    borderRadius: "8px",
    ...(isCurrentUserIsCustomer && { borderBottomRightRadius: 0 }),
    ...(!isCurrentUserIsCustomer && { borderBottomLeftRadius: 0 }),
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isCurrentUserIsCustomer ? "flex-end" : "flex-start",
        alignItems: "flex-end",
        mb: 2,
        width: "100%",
      }}
    >
      {/* Sales Avatar (on left) */}
      {!isCurrentUserIsCustomer && (
        <Avatar
          // src={salesAvatarPath}
          sx={{
            width: 32,
            height: 32,
            mr: 1,
            mb: 3,
            flexShrink: 0,
          }}
        >
          <PersonIcon />
        </Avatar>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: isCurrentUserIsCustomer ? "flex-end" : "flex-start",
          maxWidth: "70%",
        }}
      >
        <Box
          sx={{
            p: 1.5,
            ...borderRadiusStyle,
            bgcolor: isCurrentUserIsCustomer ? "#03c6c939" : "#f0f0f0",
            color: "#1F2233",
            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              fontSize: "0.9rem",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {message?.message}
          </Typography>
        </Box>

        <Typography
          variant="caption"
          sx={{
            color: "#757575",
            fontSize: "0.75rem",
            mt: 0.5,
            ...(isCurrentUserIsCustomer
              ? { mr: 0, ml: "auto" }
              : { ml: 0.5, mr: "auto" }),
          }}
        >
          {formatTimeAgo(message?.created_at)}
        </Typography>
      </Box>
    </Box>
  );
};
const MemoizedChatMessageBubble = React.memo(ChatMessageBubble);

const ConnectWithSales: React.FC<ConnectWithSalesProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { userProfile } = useSelector(authSelector);
  const activeChatSessionId = useSelector(currentSessionId);
  const { trackingId } = useSelector((state: RootState) => state.stepper);
  const [loadingChatSessions, setLoadingChatSessions] = useState(false);
  const [loadingChatMessages, setLoadingChatMessages] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [messageLimit, setMessageLimit] = useState(DEFAULT_MESSAGE_LIMIT);
  const showLoadMoreButton = messageLimit < (chatMessages?.length || 0);

  // trying to stay at bottom of the chatwindow to display latest msgs
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  /**
   * Fetches all chat sessions and selects the LATEST one to set as the active session.
   */
  const fetchAndSetLatestChatSession = async () => {
    if (!trackingId) return;

    setLoadingChatSessions(true);
    try {
      const res = await chatServices.getAllChatSessions({
        trackingId: trackingId,
      });

      const sessions: PreviousChat[] = res.data?.data || [];
      if (sessions.length > 0) {
        const sortedSessions = sessions.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        const latestSessionId = sortedSessions[0].id;

        // Only set the session ID if it's different from the current one
        if (latestSessionId && latestSessionId !== activeChatSessionId) {
          dispatch(setCurrentSessionId(latestSessionId));
        } else if (!activeChatSessionId && latestSessionId) {
          dispatch(setCurrentSessionId(latestSessionId));
        }
      } else {
        // No previous sessions found, ensure Redux state is clear to allow new chat creation
        dispatch(setCurrentSessionId(""));
      }
    } catch (error) {
      dispatch(setCurrentSessionId(""));
    } finally {
      setLoadingChatSessions(false);
    }
  };

  useEffect(() => {
    fetchAndSetLatestChatSession();
  }, []);

  // fetching all chat messages for selected session only when chatsessionid is available
  const fetchChatMessages = useCallback(async () => {
    if (!activeChatSessionId) {
      setChatMessages([]);
      return;
    }

    // Set loading state only if we are expanding history
    if (messageLimit > DEFAULT_MESSAGE_LIMIT) {
      setLoadingChatMessages(true);
    }

    try {
      const res = await chatServices.getMessagesForChat({
        chatId: activeChatSessionId,
        limit: messageLimit,
      });

      setChatMessages(res.data?.data.reverse() || []);
    } catch (error) {
      setChatMessages([]);
    } finally {
      setLoadingChatMessages(false);
    }
  }, [activeChatSessionId, messageLimit]);

  // fetch for messages in the latest active session ID
  useEffect(() => {
    let intervalId: number | undefined;

    if (activeChatSessionId) {
      fetchChatMessages();

      // Only poll if viewing the latest messages (not expanded history)
      if (messageLimit === DEFAULT_MESSAGE_LIMIT) {
        intervalId = setInterval(() => {
          fetchChatMessages();
        }, 3000) as unknown as number;
      }
    } else {
      setChatMessages([]);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [activeChatSessionId, messageLimit, fetchChatMessages]);

  // scroll to bottom after messages are fetched/updated
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    // Check if the user is near the bottom
    const isNearBottom =
      container.scrollHeight - container.clientHeight <=
      container.scrollTop + 100;

    // Scroll to bottom only if viewing the newest messages OR if the user was near the bottom before the new messages arrived.
    if (messageLimit === DEFAULT_MESSAGE_LIMIT || isNearBottom) {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [chatMessages.length, messageLimit]);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container || loadingChatMessages) return;

    // Store the height before new content is loaded
    let previousScrollHeight = container.scrollHeight;

    const handleScrollTop = () => {
      // Check if scrolled exactly to the top and we have more messages to potentially load
      if (container.scrollTop === 0 && chatMessages.length >= messageLimit) {
        container.removeEventListener("scroll", handleScrollTop);
        setMessageLimit((prevLimit) => prevLimit + DEFAULT_MESSAGE_LIMIT);
      }
    };

    container.addEventListener("scroll", handleScrollTop);

    // This logic runs *after* messages are loaded due to a limit increase, ensuring the user stays focused on the old top message.
    if (messageLimit > DEFAULT_MESSAGE_LIMIT) {
      requestAnimationFrame(() => {
        if (container) {
          // Adjust scroll position to maintain view on the message history
          container.scrollTop = container.scrollHeight - previousScrollHeight;
        }
      });
    }

    return () => {
      container.removeEventListener("scroll", handleScrollTop);
    };
  }, [loadingChatMessages, messageLimit, chatMessages.length]);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container || messageLimit === DEFAULT_MESSAGE_LIMIT) return;

    const handleScrollBottom = () => {
      // Check if scrolled near the bottom (within a 5px tolerance)
      const isAtBottom =
        container.scrollHeight - container.clientHeight <=
        container.scrollTop + 5;

      if (isAtBottom && messageLimit > DEFAULT_MESSAGE_LIMIT) {
        // User has returned to the latest message. Reset the limit to resume polling.
        setMessageLimit(DEFAULT_MESSAGE_LIMIT);
      }
    };

    container.addEventListener("scroll", handleScrollBottom);

    return () => {
      container.removeEventListener("scroll", handleScrollBottom);
    };
  }, [messageLimit]);

  // onclick refresh current chat to get latest messages
  const handleRefreshChat = () => {
    if (messageLimit !== DEFAULT_MESSAGE_LIMIT) {
      setMessageLimit(DEFAULT_MESSAGE_LIMIT);
    } else if (activeChatSessionId) {
      fetchChatMessages();
    } else {
      fetchAndSetLatestChatSession();
    }

    // Scroll to bottom immediately upon manual refresh
    scrollToBottom();
  };

  // onclick send message function
  const handleSendMessage = async () => {
    const messageToSend = newMessage?.trim();
    if (!messageToSend) return;

    const tempMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      message: messageToSend,
      source: "customer",
      created_at: new Date().toISOString(),
    };

    setChatMessages((prevMessages) => [...prevMessages, tempMessage]);
    setNewMessage("");

    try {
      if (trackingId) {
        if (activeChatSessionId) {
          // Existing chat session: Send message
          await chatServices.sendMessage(activeChatSessionId, {
            source: "customer",
            message: messageToSend,
          });
          // Re-fetch messages to update the list with the final message object
          fetchChatMessages();
        } else {
          // Fresh chat session: Create session and send message
          const createChatSessionRes = await chatServices.createChatSession({
            key: userProfile?.id,
            // salesId: salesUserId,
            trackingId: trackingId,
            typeOfApplicant: "Applicant",
          });

          const newSessionId = createChatSessionRes?.data?.data?.id;
          if (newSessionId) {
            await chatServices.sendMessage(newSessionId, {
              source: "customer",
              message: messageToSend,
            });
            dispatch(setCurrentSessionId(newSessionId));
            // Trigger full session fetch/update
            fetchAndSetLatestChatSession();
          }
        }

        setMessageLimit(DEFAULT_MESSAGE_LIMIT);
        scrollToBottom();
      }
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  // No Messages Fallback
  const NoMessagesFallback = () => (
    <Box
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        pt: 2,
        textAlign: "center",
      }}
    >
      <Typography variant="h6" sx={{ color: "#4B465C", fontWeight: 600 }}>
        {loadingChatSessions ? "Loading Chats..." : "Start a New Chat"}
      </Typography>
      <Typography variant="body2" sx={{ color: "#757575", mt: 1 }}>
        {loadingChatSessions
          ? "Please wait while we find your latest conversation."
          : "Your first message will create a new chat session."}
      </Typography>
    </Box>
  );

  return (
    <Grid
      xs={12}
      sx={{
        bgcolor: "#f5f5f5",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        [spinAnimation.slice(spinAnimation.indexOf("@keyframes"))]: true,
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "90vh",
          maxHeight: "900px",
          borderRadius: "16px",
          boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          backgroundColor: "#ffffff",
          py: 2,
        }}
      >
        {/* Full-width Chat Window */}
        <Grid
          xs={12}
          sx={{
            display: "flex",
            flexDirection: "column",
            bgcolor: "#ffffff",
            width: "100%",
          }}
        >
          <Box
            sx={{
              p: 2.5,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: "#168794",
                textAlign: "center",
              }}
            >
              Connect with Sales
            </Typography>
          </Box>

          {/* Messages Display Area */}
          {chatMessages && chatMessages.length > 0 ? (
            <Box
              ref={chatContainerRef}
              sx={{
                flexGrow: 1,
                px: 3,
                pt: 2,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#00B3B5",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
              }}
            >
              {loadingChatMessages && messageLimit > DEFAULT_MESSAGE_LIMIT && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 1.5,
                    mb: 1,
                  }}
                >
                  <Typography variant="caption" sx={{ color: "#168794" }}>
                    Loading Older Messages...
                  </Typography>
                </Box>
              )}
              {showLoadMoreButton && messageLimit === DEFAULT_MESSAGE_LIMIT && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 1.5,
                    mb: 1,
                    pointerEvents: loadingChatMessages ? "none" : "auto",
                  }}
                >
                  <Button
                    variant="text"
                    onClick={() => {
                      setMessageLimit(messageLimit + DEFAULT_MESSAGE_LIMIT);
                    }}
                    disabled={loadingChatMessages}
                    sx={{
                      textTransform: "none",
                      color: "#168794",
                      fontSize: "0.8rem",
                      "&:disabled": { opacity: 0.5 },
                    }}
                  >
                    Load More Messages
                  </Button>
                </Box>
              )}

              {chatMessages?.map((message) => (
                <MemoizedChatMessageBubble
                  key={message?.id}
                  message={message}
                />
              ))}
              <div ref={messagesEndRef} />
            </Box>
          ) : (
            <NoMessagesFallback />
          )}

          {/* INPUT AREA */}
          <Box
            sx={{
              p: 2.5,
              pt: 1,
              pb: 0,
            }}
          >
            {activeChatSessionId && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Button
                  variant="text"
                  onClick={handleRefreshChat}
                  endIcon={
                    <CachedIcon
                      sx={{
                        color: "#00B3B5",
                        fontSize: "1rem !important",
                      }}
                    />
                  }
                  sx={{
                    textTransform: "none",
                    color: "#1F2233",
                    bgcolor: "#00B3B514",
                    height: "35px",
                    width: "100px",
                    borderRadius: "8px",
                    fontWeight: 400,
                    fontSize: "13px",
                    p: "4px 8px",
                    "&:hover": {
                      bgcolor: "#00B3B525",
                      color: "#168794",
                    },
                  }}
                >
                  Refresh
                </Button>
              </Box>
            )}

            <Paper
              elevation={0}
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                borderRadius: "16px",
                bgcolor: "#ffffff",
                border: "1px solid #ffba49",
                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              <InputBase
                placeholder={
                  activeChatSessionId
                    ? "Type your message..."
                    : "Start a new conversation..."
                }
                fullWidth
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                sx={{
                  flexGrow: 1,
                  py: 0.5,
                  px: 2,
                  fontSize: "0.95rem",
                }}
              />

              <IconButton
                type="submit"
                sx={{
                  color: "#168794",
                  p: 1.5,
                  "&:hover": {
                    bgcolor: "transparent",
                    color: "#00B3B5",
                  },
                  flexShrink: 0,
                  mr: 0.5,
                }}
              >
                <SendIcon />
              </IconButton>
            </Paper>
          </Box>
        </Grid>
      </Box>
    </Grid>
  );
};

export default ConnectWithSales;
